import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  IconRepeat,
  IconArrowLeft,
  IconBan,
  IconPhoneCall,
  IconDownload,
  IconUpload,
} from '@tabler/icons-react'
import { capitalize, isEmpty, isNil, startCase } from 'lodash'
import moment from 'moment'
import pluralize from 'pluralize'

import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { StatProgressBar } from '@/components/charts/StatProgressBar/StatProgressBar'
import { PageHeader } from '@/components/headers/PageHeader'
import { Pill } from '@/components/pills/Pill'
import { ExternalLink } from '@/components/ExternalLink'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { AdvancedTablePagination } from '@/components/tables/AdvancedTable/AdvancedTablePagination'
import { formatDuration, formatTime } from '@/utils/helpers'
import {
  fetchCampaign,
  fetchCampaignStats,
  fetchCampaignCallLog,
  clearCampaignCallLog,
  fetchCampaignOptOuts,
  clearCampaignOptOuts,
  exportCampaignOptOuts,
  fetchVoiceAgents,
  CALL_LOG_PER_PAGE,
  OPT_OUTS_PER_PAGE,
} from '@/reducers/outbound/outboundCampaigns.actions'

import { UploadOptOutsModal } from './components/forms/OptOuts/UploadOptOutsModal'

import { OutreachStatTile } from './OutreachStatTile'

import './CampaignDetailPage.scss'

const STATUS_PILL_PROPS = {
  active: { success: true },
  paused: { warning: true },
  draft: { informative: true },
}

const OUTCOME_PILL_PROPS = {
  connected: { success: true },
  transferred: { brand: true },
  voicemail: { informative: true },
  no_answer: { warning: true },
  dnc_opted_out: { critical: true },
}

const callLogColumns = [
  {
    accessor: 'contact_name',
    label: 'Contact',
    format: (value) => value || '—',
  },
  {
    accessor: 'phone_number',
    label: 'Phone',
    format: (value) => value || '—',
  },
  {
    accessor: 'attempt',
    label: 'Attempt',
    format: (value) => (isNil(value) ? '—' : value),
  },
  {
    accessor: 'outcome',
    label: 'Outcome',
    format: (value) =>
      value ? (
        <Pill small {...(OUTCOME_PILL_PROPS[value] || {})}>
          {startCase(value)}
        </Pill>
      ) : (
        '—'
      ),
  },
  {
    accessor: 'disconnection_reason',
    label: 'Disconnect',
    format: (value) => (value ? startCase(value) : '—'),
  },
  {
    accessor: 'duration_seconds',
    label: 'Duration',
    format: (value) => (isNil(value) ? '—' : formatDuration(value)),
  },
  {
    accessor: 'timestamp',
    label: 'Timestamp',
    format: (value) => (isNil(value) ? '—' : formatTime(value)),
  },
  {
    accessor: 'call_id',
    label: '',
    isSortable: false,
    format: (value) => (
      <ExternalLink url={`/calls/${value}`} label="Explorer" className="blurple-link" />
    ),
  },
]

const OPT_OUT_SOURCE_LABELS = {
  call: 'Opted out on call',
  imported: 'Imported',
}

const optOutColumns = [
  {
    accessor: 'first_name',
    label: 'Contact',
    format: (value, row) => [row.first_name, row.last_name].filter(Boolean).join(' ') || '—',
  },
  {
    accessor: 'phone_number',
    label: 'Phone',
    format: (value) => value || '—',
  },
  {
    accessor: 'campaign_name',
    label: 'Campaign',
    format: (value) => value || '—',
  },
  {
    accessor: 'source',
    label: 'Source',
    format: (value) => OPT_OUT_SOURCE_LABELS[value] || '—',
  },
  {
    accessor: 'call_id',
    label: '',
    isSortable: false,
    format: (value) =>
      isNil(value) ? (
        '—'
      ) : (
        <ExternalLink url={`/calls/${value}`} label="Explorer" className="blurple-link" />
      ),
  },
]

const formatDate = (value) => {
  if (!value) {
    return null
  }
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : String(value)
}

const formatDateRange = (startDate, endDate) => {
  const start = formatDate(startDate)
  const end = formatDate(endDate)
  if (start && end) {
    return `${start} – ${end}`
  }
  return start || end || null
}

const percentOf = (value, total) => (total > 0 ? (value / total) * 100 : null)

export const CampaignDetailPage = ({ campaignId, onBack }) => {
  const dispatch = useDispatch()
  const { data, loading } = useSelector((state) => state.outboundCampaigns)
  const {
    campaign,
    voiceAgents,
    campaignStats,
    campaignCallLog,
    campaignCallLogTotal,
    campaignOptOuts,
    campaignOptOutsTotal,
  } = data
  const [callLogPage, setCallLogPage] = useState(1)
  const [optOutsPage, setOptOutsPage] = useState(1)
  const [showOptOutsUpload, setShowOptOutsUpload] = useState(false)

  // Uploaded rows land on page one. Changing the page re-runs the fetch effect,
  // so dispatch directly only when already there and the effect will not re-run.
  const handleOptOutsUploaded = () => {
    if (optOutsPage === 1) {
      dispatch(fetchCampaignOptOuts(campaignId, { limit: OPT_OUTS_PER_PAGE, offset: 0 }))
      return
    }
    setOptOutsPage(1)
  }

  useEffect(() => {
    if (!campaignId) {
      return
    }
    const loadCampaignAndStats = async () => {
      const loaded = await dispatch(fetchCampaign(campaignId))
      if (!loaded) {
        return
      }
      const startDate = loaded.start_date
        ? moment(loaded.start_date).startOf('day').format()
        : undefined
      const endDate = loaded.end_date ? moment(loaded.end_date).endOf('day').format() : undefined
      dispatch(fetchCampaignStats(loaded.id, { startDate, endDate }))
    }
    loadCampaignAndStats()
    dispatch(fetchVoiceAgents())
  }, [campaignId])

  // Clear the prior campaign's calls and reset to page 1 when switching campaigns,
  // so a slow or failed fetch never leaves the previous campaign's rows showing.
  // Paging refetches without clearing, keeping rows visible under the loading state.
  useEffect(() => {
    dispatch(clearCampaignCallLog())
    dispatch(clearCampaignOptOuts())
    setCallLogPage(1)
    setOptOutsPage(1)
  }, [campaignId])

  useEffect(() => {
    if (!campaignId) {
      return
    }
    const offset = (callLogPage - 1) * CALL_LOG_PER_PAGE
    dispatch(fetchCampaignCallLog(campaignId, { limit: CALL_LOG_PER_PAGE, offset }))
  }, [campaignId, callLogPage])

  useEffect(() => {
    if (!campaignId) {
      return
    }
    const offset = (optOutsPage - 1) * OPT_OUTS_PER_PAGE
    dispatch(fetchCampaignOptOuts(campaignId, { limit: OPT_OUTS_PER_PAGE, offset }))
  }, [campaignId, optOutsPage])

  // print-hidden: navigation chrome has no place in the PDF export, but the
  // campaign name it sits beside is the report's title.
  const backButton = (
    <Button small square className="print-hidden" onClick={onBack} data-testid="back-button">
      <IconArrowLeft />
    </Button>
  )

  if (loading.campaign || !campaign) {
    return (
      <PageHeader
        title={
          <div className="flex-align-center medium-gap">
            {backButton}
            Campaign
          </div>
        }
      />
    )
  }

  const voiceAgent = voiceAgents.find((agent) => agent.id === campaign.voice_agent_id)
  const voiceAgentName = voiceAgent ? voiceAgent.use_case_name : null
  const dateRange = formatDateRange(campaign.start_date, campaign.end_date)
  const contactListsLabel = isEmpty(campaign.contact_lists)
    ? null
    : `${pluralize('Contact List', campaign.contact_lists.length)}: ${campaign.contact_lists
        .map((list) => list.name)
        .join(', ')}`

  const subtitleParts = [voiceAgentName, dateRange, contactListsLabel].filter(Boolean)

  const totalContacts = campaignStats?.total_contacts ?? 0
  const contactsAttempted = campaignStats?.contacts_attempted ?? 0
  const callsMade = campaignStats?.calls_made ?? 0
  const callsMadeToday = campaignStats?.calls_made_today ?? 0
  const connected = campaignStats?.connected ?? 0
  const transferred = campaignStats?.transferred ?? 0
  const voicemail = campaignStats?.voicemail ?? 0
  const noAnswer = campaignStats?.no_answer ?? 0
  const dncRemovals = campaignStats?.dnc_opted_out ?? 0
  const penetrationPct = totalContacts > 0 ? (contactsAttempted / totalContacts) * 100 : 0
  const connectRate = percentOf(connected, callsMade)
  const transferRate = percentOf(transferred, callsMade)
  const voicemailRate = percentOf(voicemail, callsMade)
  const noAnswerRate = percentOf(noAnswer, callsMade)
  const dncRate = percentOf(dncRemovals, totalContacts)

  return (
    <>
      <PageHeader
        title={
          <div className="flex-align-center medium-gap">
            {backButton}
            {campaign.name}
            {campaign.status && (
              <Pill small {...(STATUS_PILL_PROPS[campaign.status] || {})}>
                {capitalize(campaign.status)}
              </Pill>
            )}
          </div>
        }
        description={subtitleParts.join(' • ')}
      />
      {campaignStats && (
        <Card className="campaign-progress-card" data-testid="list-penetration-card">
          <StatProgressBar
            label="List penetration"
            value={contactsAttempted}
            total={totalContacts}
            unit="contacts attempted"
            footerLeft={`${penetrationPct.toFixed(1)}% of list reached`}
          />
        </Card>
      )}
      {campaignStats && (
        <Card
          title="Outreach progress"
          className="outreach-progress-card"
          data-testid="outreach-progress-card"
        >
          <div className="outreach-progress-grid">
            <OutreachStatTile
              testId="stat-calls-made"
              label="Calls made"
              value={callsMade}
              footer={`+${callsMadeToday.toLocaleString()} today`}
            />
            <OutreachStatTile
              testId="stat-transferred"
              icon={<IconRepeat size={14} />}
              label="Transferred"
              value={transferred}
              variant="brand"
              footer={transferRate !== null ? `${transferRate.toFixed(1)}% of calls` : null}
            />
            <OutreachStatTile
              testId="stat-connected"
              icon={<IconPhoneCall size={14} />}
              label="Connected"
              value={connected}
              variant="success"
              footer={connectRate !== null ? `${connectRate.toFixed(1)}% connect rate` : null}
            />
            <OutreachStatTile
              testId="stat-voicemail"
              label="Voicemail"
              value={voicemail}
              footer={voicemailRate !== null ? `${voicemailRate.toFixed(1)}% of calls` : null}
            />
            <OutreachStatTile
              testId="stat-no-answer"
              label="No answer"
              value={noAnswer}
              footer={noAnswerRate !== null ? `${noAnswerRate.toFixed(1)}% of calls` : null}
            />
            <OutreachStatTile
              testId="stat-dnc-removals"
              icon={<IconBan size={14} />}
              label="DNC removals"
              value={dncRemovals}
              variant="critical"
              footer={dncRate !== null ? `${dncRate.toFixed(1)}% of list` : null}
            />
          </div>
        </Card>
      )}
      <Card title="Call log" data-testid="campaign-call-log-card">
        <AdvancedTable
          rows={campaignCallLog}
          columns={callLogColumns}
          loading={loading.campaignCallLog}
          index="call_id"
          defaultOrderBy="timestamp"
          defaultOrder="desc"
        />
        {campaignCallLogTotal > 0 && (
          <AdvancedTablePagination
            activePage={callLogPage}
            rowsPerPage={CALL_LOG_PER_PAGE}
            count={campaignCallLogTotal}
            totalPages={Math.ceil(campaignCallLogTotal / CALL_LOG_PER_PAGE)}
            setActivePage={setCallLogPage}
            label="call"
          />
        )}
      </Card>
      <Card
        title="Opt-outs"
        subtitle={
          voiceAgentName
            ? `Do Not Call list for ${voiceAgentName} — shared by every campaign using this agent`
            : 'Do Not Call list — shared by every campaign using this AI agent'
        }
        data-testid="campaign-opt-outs-card"
        buttons={
          <div className="flex-align-center small-gap">
            <Button
              square
              className="print-hidden"
              aria-label="Upload opt-outs CSV"
              tooltip="Upload opt-outs CSV"
              disabled={isNil(campaign.voice_agent_id)}
              disabledTooltip="Assign an AI agent to this campaign to upload opt-outs"
              onClick={() => setShowOptOutsUpload(true)}
              data-testid="upload-opt-outs-button"
            >
              <IconUpload />
            </Button>
            <Button
              square
              className="print-hidden"
              aria-label="Download opt-outs CSV"
              tooltip="Download opt-outs CSV"
              loading={loading.optOutsExport}
              onClick={() => dispatch(exportCampaignOptOuts(campaign.id, campaign.name))}
              data-testid="download-opt-outs-button"
            >
              <IconDownload />
            </Button>
          </div>
        }
      >
        <AdvancedTable
          rows={campaignOptOuts}
          columns={optOutColumns}
          loading={loading.campaignOptOuts}
          index="id"
          noDataHeader="No opt-outs"
          noDataText="No one has opted out of this campaign's AI agent yet."
        />
        {campaignOptOutsTotal > 0 && (
          <AdvancedTablePagination
            activePage={optOutsPage}
            rowsPerPage={OPT_OUTS_PER_PAGE}
            count={campaignOptOutsTotal}
            totalPages={Math.ceil(campaignOptOutsTotal / OPT_OUTS_PER_PAGE)}
            setActivePage={setOptOutsPage}
            label="opt-out"
            data-testid="opt-outs-pagination"
          />
        )}
      </Card>
      {showOptOutsUpload && (
        <UploadOptOutsModal
          campaign={campaign}
          voiceAgentName={voiceAgentName}
          show
          onClose={() => setShowOptOutsUpload(false)}
          onUploaded={handleOptOutsUploaded}
        />
      )}
    </>
  )
}
